/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    LocalStorage,
    BrowserUtils,
    Logger,
    IPerformanceClient,
} from "@azure/msal-browser";
import {
    ApplicationInsights,
    IExtendedConfiguration,
    IExtendedTelemetryItem,
} from "@microsoft/1ds-analytics-js";
import { PerformanceEvent, PerformanceEvents } from "./TelemetryUtils.js";

// Instrumentation Key for "MSALJS Tenant" Cluster
const instrumentationKey =
    "d634483c08244c1ca09af2b2d952c92e-ab2bba03-2ba3-49d8-a82c-ef6da750d8ab-7725";
const profileIdCacheKey = `mats-telemetry-profile-id`;

/**
 * Returns boolean flag, indicating whether additional observability should be added to event
 */
export function isObservableEvent(ratio: number): boolean {
    const randomNumber = Math.random();
    return randomNumber <= ratio;
}

export class PerformanceTelemetryReporter {
    private readonly clientId: string;
    private logger: Logger;
    private readonly eventsToEmit: Set<string>;
    private readonly miscEvents: Set<string>;
    private readonly appInsights: ApplicationInsights;
    readonly synthetic: boolean = false;
    private readonly observabilityRatio = 0.1;
    private readonly samplingRatio = 0.01;
    private readonly appName?: string;
    private readonly appVersion?: string;
    private readonly profileTelemetryId: string;
    private readonly channelId: string;
    private readonly sampleEvents: boolean;

    constructor(
        clientId: string,
        logger: Logger,
        performanceClient: IPerformanceClient,
        eventsToEmit: PerformanceEvents[],
        miscEvents: PerformanceEvents[],
        channelId: string,
        synthetic?: boolean,
        appName?: string,
        appVersion?: string
    ) {
        this.clientId = clientId;
        this.logger = logger;
        this.eventsToEmit = new Set(eventsToEmit);
        this.miscEvents = new Set(miscEvents);

        const appInsightsConfig: IExtendedConfiguration = {
            instrumentationKey,
            extensions: [],
            propertyConfiguration: {
                gpcDataSharingOptIn: false,
            },
            disableCookiesUsage: true,
        };

        this.appInsights = new ApplicationInsights();
        this.appInsights.initialize(appInsightsConfig, []);
        this.synthetic = synthetic || false;
        this.appName = appName;
        this.appVersion = appVersion;
        this.channelId = channelId;
        this.sampleEvents = Math.random() <= this.samplingRatio;
        this.logger.verbose(
            `Sampling for misc event enabled: ${this.sampleEvents}`
        );
        try {
            const localstorage = new LocalStorage(
                this.clientId,
                logger,
                performanceClient
            );
            const cachedProfileId = localstorage.getItem(profileIdCacheKey);
            if (cachedProfileId) {
                this.profileTelemetryId = cachedProfileId;
            } else {
                const guid = BrowserUtils.createGuid();
                this.profileTelemetryId = guid;
                localstorage.setItem(profileIdCacheKey, guid);
            }
        } catch (e) {
            logger.warning(
                `PerformanceTelemetryReporter: local storage is not available`
            );
            this.profileTelemetryId = "";
        }
        this.logger.verbose("PerformanceTelemetryReporter: Initialized");
    }

    sendCustomEvent(event: PerformanceEvent): void {
        if (!this.eventsToEmit.has(event.name)) {
            this.logger.verbose(
                `PerformanceTelemetryReporter: Skipping telemetry for event ${event.name}`,
                event.correlationId
            );
            return;
        }

        // Sample all network and failed events. Sample 1% of misc and cache events.
        if (
            event.success &&
            !this.sampleEvents &&
            (this.miscEvents.has(event.name) || event.fromCache === true)
        ) {
            this.logger.verbose(
                `PerformanceTelemetryReporter: Skipping telemetry for event ${event.name}`,
                event.correlationId
            );
            return;
        }

        const dataToEmit: PerformanceEvent = {
            ...event,
            synthetic: this.synthetic, // Synthetic events used for benchmarking purposes
            appName: this.appName,
            appVersion: this.appVersion,
            profileTelemetryId: this.profileTelemetryId,
            sessionId: this.channelId,
        };

        // remove additional observability data for 90% of successful events
        if (event.success && !isObservableEvent(this.observabilityRatio)) {
            delete dataToEmit.context;
            delete dataToEmit.errorStack;
            this.logger.verbose(
                `PerformanceTelemetryReporter: Sending ${dataToEmit.name} for tracking`
            );
        } else {
            this.logger.verbose(
                `PerformanceTelemetryReporter: Sending ${dataToEmit.name} with additional observability for tracking`
            );
        }

        const event1: IExtendedTelemetryItem = {
            name: event.name,
            data: dataToEmit as IExtendedTelemetryItem["data"],
        };

        this.appInsights.track(event1);
        if (event.name === PerformanceEvents.AcquireTokenPreRedirect) {
            this.appInsights.flush();
        }
    }
}
